/**
 * TenantNew — guided wizard for creating a tenant.
 *
 * Four steps:
 *   1. Agent groups (which specialty_catalog entries to seed as sub-tenants)
 *   2. Tenant details (id/name/language/brand voice)
 *   3. Platform capabilities (tenant-wide toggles)
 *   4. Review + create
 *
 * Submits everything in one POST /api/tenants call. The route loops the
 * picked groups and forks each as a sub-tenant via the existing
 * fork-from-specialty pipeline.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import { LANGUAGE_OPTIONS } from "./tenant-config-constants.js";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const STEPS = [
  { id: 1, label: "Agent groups" },
  { id: 2, label: "Tenant details" },
  { id: 3, label: "Capabilities" },
  { id: 4, label: "Review" },
];

// The wizard only surfaces the four capability toggles that are
// actionable today — locked ones (warm handoff, live transfer,
// voicemail awareness) are hidden from the wizard since they require
// extra setup the user isn't doing inside this flow. The full list
// (including locked ones) is shown on the sub-tenant Config tab.
const CAPABILITY_DEFS = [
  {
    id: "language_detection",
    label: "Language detection",
    description:
      "Detects when the patient is speaking a non-English language and offers to switch.",
  },
  {
    id: "active_listening",
    label: "Active listening",
    description:
      "Pauses when the patient is mid-thought instead of jumping in (skip_turn).",
  },
  {
    id: "barge_in",
    label: "Barge-in",
    description:
      "Patient can interrupt the agent mid-utterance and it stops talking immediately.",
  },
  {
    id: "auto_close_call",
    label: "Auto-close call",
    description:
      "Calls end_call automatically once the goodbye is said and tasks are done.",
  },
];

export default function TenantNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [allGroups, setAllGroups] = useState([]);

  const [form, setForm] = useState({
    group_ids: [],
    id: "",
    name: "",
    legal_name: "",
    default_language: "English",
    brand_voice: "",
    capabilities: {
      language_detection: true,
      active_listening: true,
      barge_in: true,
      auto_close_call: true,
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.listSpecialtyCatalog();
        setAllGroups(res.data || []);
      } catch (err) {
        setError(err);
      } finally {
        setGroupsLoading(false);
      }
    })();
  }, []);

  const effectiveId = useMemo(
    () => slugify(form.id || form.name),
    [form.id, form.name]
  );

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleGroup = (gid) => {
    setForm((f) => {
      const set = new Set(f.group_ids);
      if (set.has(gid)) set.delete(gid);
      else set.add(gid);
      return { ...f, group_ids: Array.from(set) };
    });
  };

  const toggleCapability = (capId) => {
    setForm((f) => ({
      ...f,
      capabilities: {
        ...f.capabilities,
        [capId]: !f.capabilities[capId],
      },
    }));
  };

  // Per-step validity. Drives the Next button state.
  const stepValid = (s) => {
    if (s === 1) return true; // empty allowed
    if (s === 2) return !!form.name.trim() && !!effectiveId;
    if (s === 3) return true;
    if (s === 4) return !!form.name.trim() && !!effectiveId;
    return true;
  };

  const canNext = stepValid(step);

  const next = () => {
    if (!canNext) return;
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setError(null);
    if (!stepValid(4)) {
      setError(new Error("Tenant name is required."));
      return;
    }
    setBusy(true);
    try {
      const body = {
        id: effectiveId,
        name: form.name.trim(),
        legal_name: form.legal_name.trim() || form.name.trim(),
        default_language: form.default_language,
        brand_voice: form.brand_voice.trim(),
        capabilities: form.capabilities,
        seed_groups: form.group_ids,
      };
      await api.createTenant(body);
      nav(`/tenants/${encodeURIComponent(effectiveId)}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="New Tenant"
        subtitle="Create a tenant, attach agent groups, and set tenant-wide platform capabilities."
        breadcrumbs={[
          { label: "Tenants", to: "/tenants" },
          { label: "New" },
        ]}
        actions={
          <div className="btn-row">
            <button className="btn" onClick={() => nav(-1)} disabled={busy}>
              Cancel
            </button>
          </div>
        }
      />

      <div className="page-body" style={{ paddingBottom: 96 }}>
        <ErrorBanner error={error} />

        {/* Step indicator */}
        <div
          className="card"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: "12px 16px",
            marginBottom: 16,
          }}
        >
          {STEPS.map((s, i) => {
            const active = s.id === step;
            const done = s.id < step;
            return (
              <React.Fragment key={s.id}>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    opacity: done || active ? 1 : 0.5,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: active
                        ? "#2962ff"
                        : done
                        ? "#5cb85c"
                        : "#cfd6e0",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {done ? "✓" : s.id}
                  </span>
                  <span
                    style={{
                      fontWeight: active ? 600 : 500,
                      fontSize: 13,
                    }}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      background: done ? "#5cb85c" : "#cfd6e0",
                      maxWidth: 60,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && (
          <StepGroups
            loading={groupsLoading}
            groups={allGroups}
            selected={form.group_ids}
            onToggle={toggleGroup}
          />
        )}

        {step === 2 && (
          <StepDetails
            form={form}
            effectiveId={effectiveId}
            onChange={update}
          />
        )}

        {step === 3 && (
          <StepCapabilities
            capabilities={form.capabilities}
            onToggle={toggleCapability}
          />
        )}

        {step === 4 && (
          <StepReview
            form={form}
            effectiveId={effectiveId}
            allGroups={allGroups}
          />
        )}
      </div>

      {/* Sticky footer */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "12px 24px",
          background: "#fff",
          borderTop: "1px solid var(--color-border)",
          display: "flex",
          gap: 8,
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 50,
        }}
      >
        <div className="muted small">
          Step {step} of {STEPS.length} · {STEPS[step - 1].label}
        </div>
        <div className="btn-row">
          <button
            className="btn"
            onClick={back}
            disabled={step === 1 || busy}
          >
            ← Back
          </button>
          {step < STEPS.length ? (
            <button
              className="btn primary"
              onClick={next}
              disabled={!canNext || busy}
            >
              Next →
            </button>
          ) : (
            <button
              className="btn primary"
              onClick={submit}
              disabled={busy || !stepValid(4)}
            >
              {busy ? "Creating..." : "Create tenant"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Step 1: Agent groups ───────────────────────────────────────────
function StepGroups({ loading, groups, selected, onToggle }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>
          Pick agent groups{" "}
          <span
            className="muted small"
            style={{ fontWeight: 400, marginLeft: 6 }}
          >
            {selected.length} selected
          </span>
        </h3>
      </div>
      <p className="muted small mb-16">
        Each group you pick is forked from the library catalog and attached
        to the tenant as a sub-tenant. You can leave this empty and add
        groups later — but starting here is the fastest way to get a
        tenant up and running.
      </p>
      {loading ? (
        <Loading />
      ) : groups.length === 0 ? (
        <div className="muted small" style={{ padding: "8px 0" }}>
          No agent groups in the library yet. Create one in Library →
          Agent Groups first.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {groups.map((g) => {
            const gid = g.id || g._id; // prefer string slug over ObjectId
            const on = selected.includes(gid);
            return (
              <label
                key={gid}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: 12,
                  borderRadius: "var(--radius-sm)",
                  background: on ? "#eef5ff" : "#fafafa",
                  border: `1px solid ${on ? "#c5dcff" : "var(--color-border)"}`,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onToggle(gid)}
                  style={{ marginTop: 3 }}
                />
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: g.color || "#455A64",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {(g.icon && g.icon.trim()) ||
                    (g.name || g._id).charAt(0).toUpperCase()}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {g.name || g._id}
                  </div>
                  {g.description && (
                    <div className="muted small" style={{ marginTop: 2 }}>
                      {g.description}
                    </div>
                  )}
                  {g.category && (
                    <span
                      className="badge badge-accent"
                      style={{ marginTop: 6 }}
                    >
                      {g.category}
                    </span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Tenant details ─────────────────────────────────────────
function StepDetails({ form, effectiveId, onChange }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Tenant details</h3>
      </div>
      <p className="muted small mb-16">
        The variables below are captured at setup time. Operational data
        like clinic hours, phone numbers, and addresses flows in via tool
        calls at runtime — there's no need to enter them here.
      </p>

      <div className="form-field">
        <label>Display name</label>
        <input
          type="text"
          placeholder="e.g. Riverside Oncology Center"
          value={form.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <div className="form-field">
        <label>Tenant ID (slug)</label>
        <input
          type="text"
          placeholder="auto-derived from name if blank"
          value={form.id}
          onChange={(e) => onChange({ id: e.target.value })}
        />
        <div className="help">
          Effective ID: <span className="mono">{effectiveId || "—"}</span>
        </div>
      </div>

      <div className="form-field">
        <label>Legal name (optional)</label>
        <input
          type="text"
          placeholder="defaults to display name"
          value={form.legal_name}
          onChange={(e) => onChange({ legal_name: e.target.value })}
        />
      </div>

      <div className="form-field">
        <label>Default language</label>
        <select
          value={form.default_language}
          onChange={(e) => onChange({ default_language: e.target.value })}
        >
          {LANGUAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="help">
          The agent starts the call in this language. Detection can switch it
          mid-call if the language_detection capability is on.
        </div>
      </div>

      <div className="form-field">
        <label>Brand voice (optional)</label>
        <textarea
          rows={2}
          placeholder="e.g. warm and reassuring, addresses patients by first name"
          value={form.brand_voice}
          onChange={(e) => onChange({ brand_voice: e.target.value })}
          style={{
            width: "100%",
            padding: 8,
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
            fontSize: 13,
            resize: "vertical",
          }}
        />
        <div className="help">
          One-line tone guidance injected into the orchestrator greeting.
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Platform capabilities ──────────────────────────────────
function StepCapabilities({ capabilities, onToggle }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Platform capabilities</h3>
      </div>
      <p className="muted small mb-16">
        Tenant-wide capabilities that apply to every group under this
        tenant. Turn off anything you don't want the agent to do.
      </p>
      {CAPABILITY_DEFS.map((cap) => {
        const on = !!capabilities[cap.id];
        return (
          <label
            key={cap.id}
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
              padding: "10px 12px",
              borderRadius: "var(--radius-sm)",
              background: on ? "#eef5ff" : "#fafafa",
              border: `1px solid ${on ? "#c5dcff" : "var(--color-border)"}`,
              marginBottom: 8,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => onToggle(cap.id)}
              style={{ marginTop: 3 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{cap.label}</div>
              <div className="muted small" style={{ marginTop: 2 }}>
                {cap.description}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

// ─── Step 4: Review ─────────────────────────────────────────────────
function StepReview({ form, effectiveId, allGroups }) {
  const groupLabel = (gid) => {
    const g = allGroups.find((x) => (x.id || x._id) === gid);
    return g ? g.name || gid : gid;
  };
  const langLabel =
    LANGUAGE_OPTIONS.find((o) => o.value === form.default_language)?.label ||
    form.default_language;
  const enabledCaps = CAPABILITY_DEFS.filter((c) => form.capabilities[c.id]);
  const disabledCaps = CAPABILITY_DEFS.filter((c) => !form.capabilities[c.id]);

  return (
    <div className="card">
      <div className="card-header">
        <h3>Review</h3>
      </div>
      <p className="muted small mb-16">
        Confirm everything below, then click <strong>Create tenant</strong>.
        The picked groups will be seeded as sub-tenants automatically.
      </p>

      <div className="form-field">
        <label>Tenant</label>
        <div>
          <strong>{form.name || "(no name)"}</strong>
          <span className="muted small mono" style={{ marginLeft: 8 }}>
            {effectiveId || "—"}
          </span>
        </div>
        {form.legal_name && form.legal_name !== form.name && (
          <div className="muted small">Legal: {form.legal_name}</div>
        )}
      </div>

      <div className="form-field">
        <label>Default language</label>
        <div>{langLabel}</div>
      </div>

      {form.brand_voice && (
        <div className="form-field">
          <label>Brand voice</label>
          <div className="muted small" style={{ fontStyle: "italic" }}>
            "{form.brand_voice}"
          </div>
        </div>
      )}

      <div className="form-field">
        <label>
          Agent groups{" "}
          <span className="muted small" style={{ fontWeight: 400 }}>
            {form.group_ids.length} selected
          </span>
        </label>
        {form.group_ids.length === 0 ? (
          <div className="muted small">
            None — you can attach groups later from the tenant detail page.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {form.group_ids.map((gid) => (
              <span key={gid} className="badge badge-accent">
                {groupLabel(gid)}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="form-field">
        <label>Platform capabilities</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {enabledCaps.map((c) => (
            <span key={c.id} className="badge badge-live">
              ✓ {c.label}
            </span>
          ))}
          {disabledCaps.map((c) => (
            <span key={c.id} className="badge" style={{ opacity: 0.6 }}>
              ✗ {c.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
